import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import { CoreApi } from '@inter-ikea-storageone/range';
import {
  getAngleAdjustedDirection,
  getDefaultAngleAdjustment
} from '@inter-ikea-storageone/range/src/utils/cameraUtils';
import { VectorXYZ } from '@inter-ikea-storageone/range/src/utils/vectorUtils';
import { extractRoomPartEntityIdsByComponentTypeName } from './extractRoomPartEntityIdsByComponentTypeName';
import { isDefined } from '@inter-ikea-storageone/range/src/utils/isDefined';

type ScreenshotDirectionComputationParams = {
  coreApi: CoreApi;
  screenshotEntities: Entity[];
  screenshotEntitiesInHierarchy: Entity[];
};

/**
 * @param computationParams - The parameters for computing the screenshot direction
 *  @param {CoreApi} computationParams.coreApi - The CoreApi instance
 *  @param {Entity[]} computationParams.screenshotEntities - The entities to be included in the screenshot
 *  @param {Entity[]} computationParams.screenshotEntitiesInHierarchy - The entities in the hierarchy to be included in the screenshot
 * @returns {VectorXYZ} - The computed screenshot direction
 * @description Computes the screenshot direction based on the provided entities and their hierarchy.
 * 1. It first extracts the room parts attached to the screenshot entities.
 * 2. It checks if there are multiple room parts of the same type.
 * 3. It determines if the angle should be adjusted exclusively vertically or not.
 * 4. It computes the default angle adjustment.
 */
export const computeScreenshotDirection = ({
  coreApi,
  screenshotEntities,
  screenshotEntitiesInHierarchy
}: ScreenshotDirectionComputationParams): VectorXYZ => {
  // Get room parts attached to screenshotEntities
  // Not sure if this is actually needed
  const roomParts = screenshotEntitiesInHierarchy
    .map(entity =>
      HierarchyUtils.findWithComponentInUpwardsHierarchy(
        entity,
        ComponentIds.ROOM_PART_COMPONENT_ID
      )
    )
    .filter(isDefined);

  const roomPartEntityIdsByComponentTypeName = extractRoomPartEntityIdsByComponentTypeName(
    roomParts
  );

  const hasMultipleRoomPartsWithSameType = Object.values(roomPartEntityIdsByComponentTypeName).some(
    parts => !!parts && parts.size > 1
  );

  const onlyAdjustAngleVertically =
    coreApi.entities.entityIsPartOfAngledSolution(screenshotEntitiesInHierarchy) ||
    // Take image without angle if there are multiple parts of any type
    hasMultipleRoomPartsWithSameType;

  const defaultAngleAdjustment = getDefaultAngleAdjustment();
  let screenshotDirection = getAngleAdjustedDirection(
    screenshotEntities,
    onlyAdjustAngleVertically ? { y: defaultAngleAdjustment.y } : defaultAngleAdjustment
  );

  // Adjust direction to look slightly more downward
  screenshotDirection = {
    ...screenshotDirection,
    y: screenshotDirection.y - 0.1
  };

  return screenshotDirection;
};
